import React, { useEffect, useState } from "react";
import axios from "axios";
import { Table, Spinner, Button, Input, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";
import AdminPageShell, { AdminSurface } from "../../../components/Admin/AdminPageShell";
import { getApiUrl } from "../../../utils/site";

const DeliveryAdminDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deliveryGuys, setDeliveryGuys] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const [mapModal, setMapModal] = useState(false);
  const [mapOrder, setMapOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
    fetchDeliveryGuys();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/api/orders"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveryGuys = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/api/users/delivery-guys"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeliveryGuys(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setDeliveryGuys([]);
    }
  };

  const handleAssign = async (orderId, deliveryGuyId) => {
    setAssigning(orderId);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        getApiUrl(`/api/orders/${orderId}`),
        { delivery_guy_id: deliveryGuyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchOrders();
    } catch (error) {
      alert("Failed to assign order.");
    } finally {
      setAssigning(null);
    }
  };

  const getLatLng = (order) => {
    if (!order?.delivery_map_url) return null;
    const match = order.delivery_map_url.match(/q=([\d.]+),([\d.]+)/);
    if (!match) return null;
    return [parseFloat(match[1]), parseFloat(match[2])];
  };

  const sortedOrders = [...orders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const assignedOrders = sortedOrders.filter((order) => order.delivery_guy_id);
  const unassignedOrders = sortedOrders.filter((order) => !order.delivery_guy_id);
  const deliveryInProgress = sortedOrders.filter(
    (order) => order.delivery_status && !["delivered", "cancelled"].includes(order.delivery_status)
  );
  const availableCouriers = deliveryGuys.length;

  return (
    <AdminPageShell
      eyebrow="Fulfilment control"
      title="Delivery Admin"
      description="Assign delivery staff, balance the route queue, and move fulfilment decisions faster from one cleaner dispatch board."
      actions={
        <>
          <Link to="/delivery/route" className="roha-admin-navbar__chip roha-admin-navbar__chip--primary">
            Open route desk
          </Link>
          <button type="button" className="roha-admin-navbar__chip" onClick={fetchOrders}>
            Refresh queue
          </button>
        </>
      }
    >
      <section className="roha-admin-card-grid">
        <article className="roha-admin-stat roha-admin-stat--gold">
          <div className="roha-admin-stat__body">
            <span>Total delivery orders</span>
            <strong>{orders.length}</strong>
            <p>Orders currently visible to dispatch.</p>
          </div>
        </article>

        <article className="roha-admin-stat roha-admin-stat--emerald">
          <div className="roha-admin-stat__body">
            <span>Assigned</span>
            <strong>{assignedOrders.length}</strong>
            <p>Orders already linked to a courier.</p>
          </div>
        </article>

        <article className="roha-admin-stat roha-admin-stat--ink">
          <div className="roha-admin-stat__body">
            <span>Unassigned</span>
            <strong>{unassignedOrders.length}</strong>
            <p>Orders still waiting for dispatch action.</p>
          </div>
        </article>

        <article className="roha-admin-stat roha-admin-stat--slate">
          <div className="roha-admin-stat__body">
            <span>Available couriers</span>
            <strong>{availableCouriers}</strong>
            <p>{deliveryInProgress.length} deliveries currently in motion.</p>
          </div>
        </article>
      </section>

      <AdminSurface title="Assignment board" subtitle={`${orders.length} delivery orders in queue`}>
        {loading ? (
          <div className="roha-admin-empty">
            <Spinner color="info" /> Loading orders...
          </div>
        ) : (
          <div className="roha-admin-table-wrap">
            <Table responsive className="roha-admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Address</th>
                  <th>Map</th>
                  <th>Products</th>
                  <th>Total</th>
                  <th>Order</th>
                  <th>Payment</th>
                  <th>Delivery</th>
                  <th>Assigned To</th>
                  <th>Assign</th>
                </tr>
              </thead>
              <tbody>
                {sortedOrders.length ? (
                  sortedOrders.map((order, idx) => {
                      const assignedGuy = deliveryGuys.find((g) => g.id === order.delivery_guy_id);
                      return (
                        <tr key={order.id}>
                          <td data-label="#">{idx + 1}</td>
                          <td data-label="Customer">{order.customer_name}</td>
                          <td data-label="Phone">{order.customer_phone || "-"}</td>
                          <td data-label="Address">
                            {order.delivery_address_line1}
                            <br />
                            {order.delivery_city}, {order.delivery_state}
                          </td>
                          <td data-label="Map">
                            {order.delivery_map_url && getLatLng(order) ? (
                              <Button color="primary" size="sm" onClick={() => { setMapOrder(order); setMapModal(true); }}>
                                View map
                              </Button>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td data-label="Products">
                            <ul className="mb-0 ps-3 roha-admin-table-list">
                              {(order.items || []).map((item, i) => (
                                <li key={i}>
                                  {item.product_name} x {item.quantity}
                                </li>
                              ))}
                            </ul>
                          </td>
                          <td data-label="Total">ETB {order.total}</td>
                          <td data-label="Order"><span className="roha-admin-pill">{order.status}</span></td>
                          <td data-label="Payment"><span className="roha-admin-pill">{order.payment_status}</span></td>
                          <td data-label="Delivery"><span className="roha-admin-pill">{order.delivery_status}</span></td>
                          <td data-label="Assigned To">{assignedGuy ? `${assignedGuy.full_name} (${assignedGuy.email})` : "Unassigned"}</td>
                          <td data-label="Assign">
                            <Input
                              type="select"
                              disabled={assigning === order.id}
                              onChange={(e) => handleAssign(order.id, e.target.value)}
                              value={order.delivery_guy_id || ""}
                            >
                              <option value="">Assign to...</option>
                              {deliveryGuys.map((guy) => (
                                <option key={guy.id} value={guy.id}>
                                  {guy.full_name} ({guy.email})
                                </option>
                              ))}
                            </Input>
                          </td>
                        </tr>
                      );
                    })
                ) : (
                  <tr>
                    <td colSpan="12" className="roha-admin-empty">
                      No deliveries available.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </AdminSurface>

      <Modal isOpen={mapModal} toggle={() => setMapModal(false)} size="lg">
        <ModalHeader toggle={() => setMapModal(false)}>Delivery location map</ModalHeader>
        <ModalBody>
          {mapOrder && getLatLng(mapOrder) ? (
            <div style={{ height: 350, width: "100%" }}>
              <MapContainer center={getLatLng(mapOrder)} zoom={15} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker
                  position={getLatLng(mapOrder)}
                  icon={L.icon({
                    iconUrl: "https://cdn-icons-png.flaticon.com/512/854/854878.png",
                    iconSize: [32, 32],
                  })}
                >
                  <Popup>Delivery location</Popup>
                </Marker>
              </MapContainer>
            </div>
          ) : (
            <div>No map data available.</div>
          )}
        </ModalBody>
        <ModalFooter>
          {mapOrder?.delivery_map_url ? (
            <a href={mapOrder.delivery_map_url} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              Get directions
            </a>
          ) : null}
          <Button color="secondary" onClick={() => setMapModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </AdminPageShell>
  );
};

export default DeliveryAdminDashboard;
